import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
export const worksheetSources = [
  'mother.name',
  'mother.dob',
  'mother.mrn',
  'pregnancy.edd',
  'birth.at',
  'birth.sex',
  'birth.method',
  'birth.site',
  'birth.attendant',
  'birth.ga_days',
  'birth.weight_g',
  'birth.length_cm',
  'birth.head_circ_cm',
  'birth.apgar_1',
  'birth.apgar_5',
] as const;
export interface StateWorksheetMapping {
  id: string;
  version: number;
  jurisdiction: string;
  title: string;
  citation: string;
  clinically_reviewed: boolean;
  fields: { key: string; label: string; source: string }[];
}
export function StateWorksheetConfigEditor({
  value,
  onChange,
}: {
  value: StateWorksheetMapping[];
  onChange: (v: StateWorksheetMapping[]) => void;
}) {
  function patch(index: number, change: Partial<StateWorksheetMapping>) {
    onChange(value.map((m, i) => (i === index ? { ...m, ...change } : m)));
  }
  return (
    <details>
      <summary>Jurisdiction birth certificate worksheet mappings</summary>
      <p className="my-2 text-sm">
        Add fields from the applicable official worksheet and document the source. No state forms are supplied
        automatically. Once published, changing a mapping requires a new edition. Worksheets populate only from signed
        birth summaries.
      </p>
      {value.map((m, i) => (
        <fieldset key={m.id} className="my-3 flex flex-col gap-3 rounded border p-3">
          <legend>{m.title || 'New mapping'}</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {(['jurisdiction', 'title', 'citation'] as const).map((key) => (
              <label key={key}>
                {key === 'citation' ? 'Official source / edition' : key}
                <Input
                  className="min-h-11"
                  value={m[key]}
                  required
                  onChange={(e) => patch(i, { [key]: e.target.value, clinically_reviewed: false })}
                />
              </label>
            ))}
            <label>
              Edition
              <Input
                className="min-h-11"
                type="number"
                min={1}
                value={m.version}
                onChange={(e) => patch(i, { version: Number(e.target.value), clinically_reviewed: false })}
              />
            </label>
          </div>
          {m.fields.map((f, j) => (
            <div key={j} className="grid gap-2 sm:grid-cols-3">
              <label>
                Official field identifier
                <Input
                  className="min-h-11"
                  required
                  value={f.key}
                  onChange={(e) =>
                    patch(i, {
                      fields: m.fields.map((x, k) => (k === j ? { ...x, key: e.target.value } : x)),
                      clinically_reviewed: false,
                    })
                  }
                />
              </label>
              <label>
                Field label
                <Input
                  className="min-h-11"
                  required
                  value={f.label}
                  onChange={(e) =>
                    patch(i, {
                      fields: m.fields.map((x, k) => (k === j ? { ...x, label: e.target.value } : x)),
                      clinically_reviewed: false,
                    })
                  }
                />
              </label>
              <label>
                Signed source
                <select
                  className="block min-h-11 w-full rounded border p-2"
                  value={f.source}
                  onChange={(e) =>
                    patch(i, {
                      fields: m.fields.map((x, k) => (k === j ? { ...x, source: e.target.value } : x)),
                      clinically_reviewed: false,
                    })
                  }>
                  {worksheetSources.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
              <Button
                type="button"
                variant="outline"
                onClick={() => patch(i, { fields: m.fields.filter((_, k) => k !== j), clinically_reviewed: false })}>
                Remove field
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                patch(i, {
                  fields: [...m.fields, { key: '', label: '', source: 'mother.name' }],
                  clinically_reviewed: false,
                })
              }>
              Add field
            </Button>
            <Button type="button" variant="outline" onClick={() => onChange(value.filter((_, k) => k !== i))}>
              Remove mapping
            </Button>
          </div>
          <label className="flex gap-2">
            <input
              type="checkbox"
              checked={m.clinically_reviewed}
              onChange={(e) => patch(i, { clinically_reviewed: e.target.checked })}
            />
            Our authorized reviewer verified this mapping against its official source.
          </label>
        </fieldset>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={() =>
          onChange([
            ...value,
            {
              id: crypto.randomUUID(),
              version: 1,
              jurisdiction: '',
              title: '',
              citation: '',
              clinically_reviewed: false,
              fields: [{ key: '', label: '', source: 'mother.name' }],
            },
          ])
        }>
        Add jurisdiction mapping
      </Button>
    </details>
  );
}
